'use client'

import { useQuery, UseQueryExecute } from '@urql/next'
import { useMemo } from 'react'

import { GetUserTokensDocument, ShowcaseFragmentFragment, GetProjectFiltersQuery } from '@/gql/generated/graphql'
import { constructUserTokenGridDocumentVariables, ValidUserTokensSort } from '../user-token-grid/utils'

import { ShowcaseDisplayView } from './showcase-display-view'
import { ShowcaseEditorView } from './showcase-editor-view'
import { useShowcaseEditor } from './use-showcase-editor'

type ShowcaseGridProps = {
  profileId: number
  walletAddresses: string[]
  isProfileOwner: boolean
  showcase?: ShowcaseFragmentFragment
  showcaseFetching: boolean
  reexecuteShowcases: UseQueryExecute
  projectFiltersData?: GetProjectFiltersQuery
}

export function ShowcaseGrid({
  profileId,
  walletAddresses,
  isProfileOwner,
  showcase,
  showcaseFetching,
  reexecuteShowcases,
  projectFiltersData
}: ShowcaseGridProps): React.ReactElement {
  // Query variables for user tokens (when in edit mode)
  const userTokenQueryVariables = useMemo(() => {
    return constructUserTokenGridDocumentVariables({
      sortOrder: ValidUserTokensSort.newest_collection,
      pageNumber: 1,
      tagsFilterValue: [],
      categoryFilterValue: [],
      walletAddresses,
      limit: 999
    })
  }, [walletAddresses])

  const showcaseItems = showcase?.showcase_items ?? []
  const showcaseTokens = showcaseItems.map((item) => item.token)

  // Query for user tokens - always query to have them available for the hook
  const [userTokensRes] = useQuery({
    query: GetUserTokensDocument,
    variables: userTokenQueryVariables
  })

  const { data: userTokensData, fetching: userTokensFetching } = userTokensRes

  // WORKAROUND: This filter is necessary due to a Hasura permission issue.
  // Inactive projects are returned with a null `project` field, even when the
  // user has permission to view them. This filter removes these invalid rows.
  const userTokens = useMemo(
    () => userTokensData?.tokens_metadata.filter((token) => Boolean(token.project)) ?? [],
    [userTokensData]
  )

  // Initialize showcase editor
  const {
    isEditMode,
    isSaving,
    selectedTokenIds,
    visibleTokenCount,
    handleEditShowcase,
    handleCreateShowcase,
    handleToggleToken,
    handleDeselectAll,
    handleSaveShowcase,
    handleShowMore
  } = useShowcaseEditor({
    profileId,
    showcase,
    showcaseTokens,
    userTokens,
    onSaveSuccess: () => {
      reexecuteShowcases({ requestPolicy: 'cache-and-network' })
    }
  })

  // Render editor view when in edit mode
  if (isEditMode) {
    return (
      <ShowcaseEditorView
        userTokens={userTokens}
        selectedTokenIds={selectedTokenIds}
        isSaving={isSaving}
        userTokensFetching={userTokensFetching}
        visibleTokenCount={visibleTokenCount}
        projectFiltersData={projectFiltersData}
        onToggleToken={handleToggleToken}
        onDeselectAll={handleDeselectAll}
        onSave={handleSaveShowcase}
        onShowMore={handleShowMore}
      />
    )
  }

  // Render display view
  return (
    <ShowcaseDisplayView
      showcase={showcase}
      showcaseTokens={showcaseTokens}
      isProfileOwner={isProfileOwner}
      showcaseFetching={showcaseFetching}
      ownsAnyTokens={userTokens.length > 0}
      onEditShowcase={handleEditShowcase}
      onCreateShowcase={handleCreateShowcase}
    />
  )
}
